import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authFetch } from '../utils/authFetch';
import { 
  Building2, 
  MessageCircle, 
  MessageSquare,
  CheckCircle, 
  XCircle, 
  Clock, 
  Send, 
  FileText, 
  Loader2, 
  AlertCircle,
  RefreshCw,
  Download,
  Settings,
  Users,
  Phone,
  ChevronDown,
  ChevronUp,
  Key,
  Eye,
  EyeOff,
  Save,
  Trash2,
  Wifi,
  WifiOff,
  QrCode,
  Link2,
  Unlink
} from 'lucide-react';

export default function Integrations() {
  const [activeTab, setActiveTab] = useState('esocial');
  const [loading, setLoading] = useState(true);
  
  // e-Social state
  const [esocialStatus, setEsocialStatus] = useState(null);
  const [esocialEvents, setEsocialEvents] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('S-2200');
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);
  
  // WhatsApp state
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messagePhone, setMessagePhone] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [qrError, setQrError] = useState(null);
  
  // Z-API Configuration state
  const [zapiConfig, setZapiConfig] = useState(null);
  const [zapiFormData, setZapiFormData] = useState({
    instance_id: '',
    token: '',
    client_token: ''
  });
  const [showToken, setShowToken] = useState(false);
  const [savingZapiConfig, setSavingZapiConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [showAdvancedMode, setShowAdvancedMode] = useState(false); // Hide technical fields by default
  
  // Employees for selection
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [esocialStatusRes, esocialEventsRes, whatsappStatusRes, whatsappMessagesRes, employeesRes, zapiConfigRes] = await Promise.all([
        authFetch('/api/esocial/status'),
        authFetch('/api/esocial/events'),
        authFetch('/api/whatsapp/status'),
        authFetch('/api/whatsapp/messages?limit=20'),
        authFetch('/api/employees'),
        authFetch('/api/zapi-config')
      ]);

      if (esocialStatusRes.ok) setEsocialStatus(await esocialStatusRes.json());
      if (esocialEventsRes.ok) setEsocialEvents(await esocialEventsRes.json());
      if (whatsappStatusRes.ok) setWhatsappStatus(await whatsappStatusRes.json());
      if (whatsappMessagesRes.ok) setWhatsappMessages(await whatsappMessagesRes.json());
      if (employeesRes.ok) {
        const emps = await employeesRes.json();
        setEmployees(emps.filter(e => e.status === 'ativo'));
      }
      if (zapiConfigRes.ok) {
        const config = await zapiConfigRes.json();
        setZapiConfig(config);
        // Update connection status from stored config
        if (config) {
          setConnectionTestResult({
            connected: config.is_connected,
            phone: config.connected_phone,
            last_checked: config.last_checked
          });
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createEsocialEvent = async () => {
    if (!selectedEmployee) {
      alert('Selecione um funcionário');
      return;
    }
    
    setCreatingEvent(true);
    try {
      const response = await authFetch('/api/esocial/events', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: selectedEmployee,
          event_type: selectedEventType
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Evento criado com sucesso!\nProtocolo: ${data.event?.protocolo}`);
        fetchData();
        setSelectedEmployee('');
      } else {
        const error = await response.json();
        alert(`Erro: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Erro ao criar evento');
    } finally {
      setCreatingEvent(false);
    }
  };

  const sendWhatsAppMessage = async () => {
    if (!messagePhone || !messageContent) {
      alert('Preencha o telefone e a mensagem');
      return;
    }
    
    setSendingMessage(true);
    try {
      const response = await authFetch('/api/whatsapp/send', {
        method: 'POST',
        body: JSON.stringify({
          phone: messagePhone,
          message: messageContent
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.success ? 'Mensagem enviada com sucesso!' : 'Mensagem registrada');
        fetchData();
        setMessagePhone('');
        setMessageContent('');
      } else {
        const error = await response.json();
        alert(`Erro: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchQrCode = async () => {
    setLoadingQr(true);
    setQrError(null);
    try {
      const response = await authFetch('/api/zapi-config/qr-code');
      
      if (response.ok) {
        const data = await response.json();
        if (data.connected) {
          setQrCode(null);
          setConnectionTestResult({ connected: true, phone: data.phone });
          toast.success('WhatsApp já está conectado!');
        } else {
          setQrCode(data.qr_code_base64 || data.qr_code);
        }
      } else {
        const error = await response.json();
        setQrError(error.detail);
        toast.error(error.detail || 'Erro ao obter QR Code');
      }
    } catch (error) {
      console.error('Error fetching QR code:', error);
      setQrError('Erro ao buscar QR Code');
      toast.error('Erro ao buscar QR Code');
    } finally {
      setLoadingQr(false);
    }
  };

  // Z-API Configuration Functions
  const saveZapiConfig = async () => {
    if (!zapiFormData.instance_id || !zapiFormData.token) {
      toast.error('Instance ID e Token são obrigatórios');
      return;
    }
    
    setSavingZapiConfig(true);
    try {
      const response = await authFetch('/api/zapi-config', {
        method: 'POST',
        body: JSON.stringify(zapiFormData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setZapiConfig(data);
        setZapiFormData({ instance_id: '', token: '', client_token: '' });
        toast.success('Configuração Z-API salva com sucesso!');
        fetchData(); // Refresh all data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar configuração');
      }
    } catch (error) {
      console.error('Error saving Z-API config:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingZapiConfig(false);
    }
  };

  const testZapiConnection = async () => {
    setTestingConnection(true);
    setConnectionTestResult(null);
    try {
      const response = await authFetch('/api/zapi-config/test', {
        method: 'POST'
      });
      
      const data = await response.json();
      setConnectionTestResult(data);
      
      if (data.success && data.connected) {
        toast.success(`Conectado! Telefone: ${data.phone}`);
      } else if (data.success && !data.connected) {
        toast.warning('Instância não conectada. Escaneie o QR Code.');
      } else {
        toast.error(data.message || 'Erro ao testar conexão');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Erro ao testar conexão');
    } finally {
      setTestingConnection(false);
    }
  };

  const deleteZapiConfig = async () => {
    if (!window.confirm('Tem certeza que deseja remover a configuração Z-API? As mensagens WhatsApp não serão mais enviadas.')) {
      return;
    }
    
    try {
      const response = await authFetch('/api/zapi-config', {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setZapiConfig(null);
        setConnectionTestResult(null);
        toast.success('Configuração Z-API removida');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao remover configuração');
      }
    } catch (error) {
      console.error('Error deleting Z-API config:', error);
      toast.error('Erro ao remover configuração');
    }
  };

  const disconnectWhatsApp = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) return;
    
    try {
      const response = await authFetch('/api/zapi-config/disconnect', {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('WhatsApp desconectado com sucesso');
        setQrCode(null);
        setConnectionTestResult({ connected: false });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao desconectar');
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Erro ao desconectar');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      processado: { label: 'Processado', variant: 'success', icon: CheckCircle },
      enviado: { label: 'Enviado', variant: 'default', icon: Send },
      sent: { label: 'Enviado', variant: 'success', icon: CheckCircle },
      pendente: { label: 'Pendente', variant: 'secondary', icon: Clock },
      pending: { label: 'Pendente', variant: 'secondary', icon: Clock },
      rejeitado: { label: 'Rejeitado', variant: 'destructive', icon: XCircle },
      entregue: { label: 'Entregue', variant: 'success', icon: CheckCircle },
      delivered: { label: 'Entregue', variant: 'success', icon: CheckCircle },
      lido: { label: 'Lido', variant: 'success', icon: CheckCircle },
      read: { label: 'Lido', variant: 'success', icon: CheckCircle },
      falha: { label: 'Falha', variant: 'destructive', icon: XCircle },
      failed: { label: 'Falha', variant: 'destructive', icon: XCircle },
      error: { label: 'Erro', variant: 'destructive', icon: XCircle }
    };
    const cfg = config[status?.toLowerCase()] || config.pendente;
    const Icon = cfg.icon;
    
    return (
      <Badge variant={cfg.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {cfg.label}
      </Badge>
    );
  };

  const eventTypeLabels = {
    'S-2200': 'Admissão',
    'S-2206': 'Alteração Contratual',
    'S-2230': 'Afastamento',
    'S-2299': 'Desligamento'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrações</h1>
          <p className="text-gray-500">Gerencie integrações com e-Social e WhatsApp</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('esocial')}
            className={`pb-3 px-1 font-medium flex items-center gap-2 transition-colors ${
              activeTab === 'esocial' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Building2 className="h-4 w-4" />
            e-Social
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`pb-3 px-1 font-medium flex items-center gap-2 transition-colors ${
              activeTab === 'whatsapp' 
                ? 'text-green-600 border-b-2 border-green-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </button>
        </div>
      </div>

      {/* e-Social Tab */}
      {activeTab === 'esocial' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Status Card */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Status da Integração
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Modo:</span>
                <Badge variant={esocialStatus?.integration_mode === 'PRODUCAO' ? 'success' : 'secondary'}>
                  {esocialStatus?.integration_mode || 'N/A'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Certificado Digital:</span>
                {esocialStatus?.certificado_digital ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Ambiente:</span>
                <span className="text-sm">{esocialStatus?.ambiente}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total de Eventos:</span>
                <span className="font-bold">{esocialStatus?.total_eventos || 0}</span>
              </div>
              
              {esocialStatus?.instrucoes_producao && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-2">Para ativar produção:</p>
                  <ol className="text-xs text-amber-700 space-y-1">
                    {esocialStatus.instrucoes_producao.map((inst, i) => (
                      <li key={i}>{inst}</li>
                    ))}
                  </ol>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create Event Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Criar Evento e-Social
              </CardTitle>
              <CardDescription>
                Gera XML no formato oficial e simula envio ao governo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Funcionário
                  </label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="w-full border rounded-md p-2"
                    data-testid="esocial-employee-select"
                  >
                    <option value="">Selecione...</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.nome} - {emp.cargo}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Evento
                  </label>
                  <select
                    value={selectedEventType}
                    onChange={(e) => setSelectedEventType(e.target.value)}
                    className="w-full border rounded-md p-2"
                    data-testid="esocial-event-type-select"
                  >
                    <option value="S-2200">S-2200 - Admissão</option>
                    <option value="S-2206">S-2206 - Alteração Contratual</option>
                    <option value="S-2230">S-2230 - Afastamento</option>
                    <option value="S-2299">S-2299 - Desligamento</option>
                  </select>
                </div>
              </div>
              <Button 
                onClick={createEsocialEvent} 
                disabled={creatingEvent || !selectedEmployee}
                data-testid="create-esocial-event-btn"
              >
                {creatingEvent ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Criar Evento
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Events List */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle>Histórico de Eventos</CardTitle>
            </CardHeader>
            <CardContent>
              {esocialEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum evento e-Social criado ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {esocialEvents.map((event) => (
                    <div key={event.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {eventTypeLabels[event.event_type] || event.event_type} - {event.employee_nome}
                            </p>
                            <p className="text-sm text-gray-500">
                              Protocolo: {event.protocolo}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(event.status)}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                          >
                            {expandedEvent === event.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                      {expandedEvent === event.id && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm"><strong>Recibo:</strong> {event.recibo || 'N/A'}</p>
                          <p className="text-sm"><strong>Data Criação:</strong> {new Date(event.created_at).toLocaleString('pt-BR')}</p>
                          {event.xml_content && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-sm text-blue-600">Ver XML</summary>
                              <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-60">
                                {event.xml_content}
                              </pre>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Z-API Configuration Card */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-green-600" />
                WhatsApp Business
              </CardTitle>
              <CardDescription>
                Conexão com WhatsApp via Z-API
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {zapiConfig ? (
                // Config exists - show simplified status view
                <div className="space-y-4">
                  {/* Connection Status - Main Focus */}
                  <div className={`p-4 rounded-lg border-2 ${
                    connectionTestResult?.connected 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {connectionTestResult?.connected ? (
                          <>
                            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="font-semibold text-green-800">Conectado</span>
                          </>
                        ) : (
                          <>
                            <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                            <span className="font-semibold text-amber-800">Desconectado</span>
                          </>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={testZapiConnection}
                        disabled={testingConnection}
                        className="h-8 px-2"
                      >
                        {testingConnection ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    
                    {connectionTestResult?.connected && connectionTestResult?.phone && (
                      <div className="flex items-center gap-2 text-sm text-green-700">
                        <Smartphone className="h-4 w-4" />
                        <span>Número vinculado: <strong>{connectionTestResult.phone}</strong></span>
                      </div>
                    )}
                  </div>
                  
                  {/* QR Code Section - Show when disconnected */}
                  {!connectionTestResult?.connected && (
                    <div className="space-y-3">
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-700 mb-1">
                          Escaneie o QR Code
                        </p>
                        <p className="text-xs text-slate-500">
                          Abra o WhatsApp no seu celular e escaneie
                        </p>
                      </div>
                      
                      {qrCode ? (
                        <div className="flex flex-col items-center p-4 bg-white border-2 border-dashed border-slate-200 rounded-lg">
                          <img 
                            src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                            alt="WhatsApp QR Code"
                            className="w-48 h-48 object-contain"
                            data-testid="whatsapp-qr-code"
                          />
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={fetchQrCode}
                            className="mt-3"
                            disabled={loadingQr}
                          >
                            {loadingQr ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-1" />
                            )}
                            Atualizar QR Code
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          onClick={fetchQrCode} 
                          disabled={loadingQr}
                          className="w-full bg-green-600 hover:bg-green-700"
                          data-testid="get-qr-code-btn"
                        >
                          {loadingQr ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <QrCode className="h-4 w-4 mr-2" />
                          )}
                          Gerar QR Code
                        </Button>
                      )}
                      {qrError && (
                        <p className="text-xs text-red-500 text-center">{qrError}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Disconnect Button - Show when connected */}
                  {connectionTestResult?.connected && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={disconnectWhatsApp}
                      className="w-full text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="disconnect-whatsapp-btn"
                    >
                      <Unlink className="h-4 w-4 mr-2" />
                      Desconectar WhatsApp
                    </Button>
                  )}
                  
                  {/* Advanced Mode Toggle */}
                  <div className="border-t pt-3 mt-3">
                    <button
                      onClick={() => setShowAdvancedMode(!showAdvancedMode)}
                      className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 w-full justify-center"
                    >
                      <Settings className="h-3 w-3" />
                      {showAdvancedMode ? 'Ocultar configurações avançadas' : 'Configurações avançadas'}
                    </button>
                    
                    {showAdvancedMode && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">Instance ID:</span>
                          <span className="font-mono text-slate-700">{zapiConfig.instance_id?.substring(0, 20)}...</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">Token:</span>
                          <span className="font-mono text-slate-700">{zapiConfig.token_preview || '••••••••'}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2 text-red-600 hover:bg-red-50"
                          onClick={deleteZapiConfig}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remover Configuração
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // No config - show setup instructions
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                    <MessageSquare className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">
                      WhatsApp não configurado
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Configure a integração Z-API para enviar mensagens
                    </p>
                  </div>
                  
                  {/* Advanced Mode for Setup */}
                  <button
                    onClick={() => setShowAdvancedMode(!showAdvancedMode)}
                    className="flex items-center gap-2 text-sm text-green-600 hover:text-green-700 w-full justify-center font-medium"
                  >
                    <Key className="h-4 w-4" />
                    {showAdvancedMode ? 'Ocultar formulário' : 'Configurar credenciais Z-API'}
                  </button>
                  
                  {showAdvancedMode && (
                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
                      <div>
                        <Label htmlFor="instance_id" className="text-sm">
                          Instance ID <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="instance_id"
                          placeholder="Ex: 3D7E8A9B1C2F..."
                          value={zapiFormData.instance_id}
                          onChange={(e) => setZapiFormData(prev => ({ ...prev, instance_id: e.target.value }))}
                          className="font-mono text-sm"
                          data-testid="zapi-instance-id-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="token" className="text-sm">
                          Token <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="token"
                            type={showToken ? 'text' : 'password'}
                            placeholder="Ex: A1B2C3D4E5F6..."
                            value={zapiFormData.token}
                            onChange={(e) => setZapiFormData(prev => ({ ...prev, token: e.target.value }))}
                            className="font-mono text-sm pr-10"
                            data-testid="zapi-token-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowToken(!showToken)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="client_token" className="text-sm">
                          Client Token <span className="text-slate-400">(opcional)</span>
                        </Label>
                        <Input
                          id="client_token"
                          type="password"
                          placeholder="Opcional para algumas contas"
                          value={zapiFormData.client_token}
                          onChange={(e) => setZapiFormData(prev => ({ ...prev, client_token: e.target.value }))}
                          className="font-mono text-sm"
                        />
                      </div>
                      
                      <Button 
                        onClick={saveZapiConfig}
                        disabled={savingZapiConfig || !zapiFormData.instance_id || !zapiFormData.token}
                        className="w-full bg-green-600 hover:bg-green-700"
                        data-testid="save-zapi-config-btn"
                      >
                        {savingZapiConfig ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Salvar Configuração
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Send Message Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Enviar Mensagem WhatsApp
              </CardTitle>
              <CardDescription>
                {connectionTestResult?.connected 
                  ? '✅ Conectado - Envie mensagens reais via WhatsApp'
                  : zapiConfig 
                    ? '⚠️ Escaneie o QR Code para conectar'
                    : '⚠️ Configure o Z-API primeiro'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="phone" className="text-sm">
                  Telefone (com DDD)
                </Label>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-slate-400" />
                  <Input
                    id="phone"
                    placeholder="11999998888"
                    value={messagePhone}
                    onChange={(e) => setMessagePhone(e.target.value)}
                    disabled={!zapiConfig}
                    data-testid="whatsapp-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="message" className="text-sm">
                  Mensagem
                </Label>
                <Textarea
                  id="message"
                  placeholder="Digite sua mensagem..."
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  rows={4}
                  disabled={!zapiConfig}
                  data-testid="whatsapp-message-input"
                />
              </div>
              <Button 
                onClick={sendWhatsAppMessage} 
                disabled={sendingMessage || !messagePhone || !messageContent || !zapiConfig}
                className="bg-green-600 hover:bg-green-700"
                data-testid="send-whatsapp-btn"
              >
                {sendingMessage ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Enviar Mensagem
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Messages List */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle>Histórico de Mensagens</CardTitle>
            </CardHeader>
            <CardContent>
              {whatsappMessages.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma mensagem enviada ainda</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Data</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Telefone</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Mensagem</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Tipo</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {whatsappMessages.map((msg) => (
                        <tr key={msg.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                          <td className="py-3 px-4 text-sm">
                            {new Date(msg.created_at || msg.sent_at).toLocaleString('pt-BR')}
                          </td>
                          <td className="py-3 px-4 text-sm font-mono">
                            {msg.phone || msg.phone_number || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm max-w-xs truncate">
                            {msg.message || msg.message_content || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <Badge variant="outline">{msg.message_type || msg.type || 'mensagem'}</Badge>
                          </td>
                          <td className="py-3 px-4">
                            {getStatusBadge(msg.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
